import { SkyLoader } from "@skypointcloud/skypoint-ui";

/**
 * The two places a spinner goes, so nobody has to invent a third.
 *
 * Before this there were seven ways of centring one across the app, and about
 * fifty sites that did not centre at all: some were bare `<SkyLoader />` with
 * no wrapper, landing top-left of the content area; more had
 * `flex justify-center` WITHOUT `items-center`, which centres horizontally and
 * leaves the spinner pinned to the top. That is the "not centred and middled"
 * you see — a spinner halfway across and nowhere down.
 *
 * ── Why two, not one ──────────────────────────────────────────────────────
 *
 * `PageLoader` is for a route that has nothing on screen yet: the whole view
 * is the spinner, so it should sit in the middle of the page.
 *
 * `SectionLoader` is for a panel, tab or table loading INSIDE a page that has
 * already drawn its header and tabs. Filling the viewport there would shove
 * the chrome around and make the spinner land below the fold — centred, and
 * wrong. It centres in the space it is given instead.
 *
 * Picking between them is the only decision a caller has to make, and getting
 * it wrong is visible immediately rather than subtly off.
 *
 * ── The height ────────────────────────────────────────────────────────────
 *
 * The header is offset by AppLayout's own padding (56px on mobile, 52px from
 * `md` up, plus the new-version strip while it shows), so a page loader
 * measures the viewport MINUS that, and no more. Using plain
 * `h-screen` — as one page did — makes the spinner sit a half-header too low
 * and adds a scrollbar to a page that has nothing to scroll.
 *
 * `h-full` is deliberately not used: it resolves against a parent's definite
 * height, and the content wrapper has only a `min-height`, so it collapses to
 * auto and centres nothing.
 */

interface LoadingProps {
  /** Shown under the spinner. Say what is loading, not that it is loading. */
  label?: string;
  /** Extra classes for the wrapper, for the rare page that needs a background. */
  className?: string;
}

/**
 * Centred in the content area, both ways. For a route with nothing drawn yet.
 *
 * The two heights match AppLayout's two header offsets: 56px on mobile where
 * the bar is `pt-14`, 52px from `md` up.
 */
export function PageLoader({ label = "Loading...", className = "" }: LoadingProps) {
  return (
    <div
      className={`flex w-full items-center justify-center min-h-[calc(100vh-56px-var(--new-version-h))] md:min-h-[calc(100vh-52px-var(--new-version-h))] ${className}`}
      role="status"
      aria-live="polite"
    >
      <SkyLoader
        label={label}
        size="large"
      />
    </div>
  );
}

/**
 * Centred in whatever box it is given. For a tab or panel inside a live page.
 *
 * `flex-1` lets it take the slack when its parent is a flex column, and the
 * min-height keeps it from collapsing to the spinner's own size when the
 * parent has no height to give — which is what makes a "centred" spinner look
 * top-aligned in a short container.
 */
export function SectionLoader({ label = "Loading...", className = "" }: LoadingProps) {
  return (
    <div
      className={`flex w-full flex-1 items-center justify-center min-h-[240px] ${className}`}
      role="status"
      aria-live="polite"
    >
      <SkyLoader
        label={label}
        size="large"
      />
    </div>
  );
}

/**
 * Inline, for a spinner that sits beside or inside something else — a button,
 * a row, a field. It centres nothing on purpose: the thing around it already
 * decided where it goes.
 */
export function InlineLoader({ label }: { label?: string }) {
  return (
    <SkyLoader
      label={label}
      size="tiny"
    />
  );
}
